import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { AdvancedCommerceListingFiltersAttributeComponentDef } from '@df/module-advanced-commerce';
import { PriceService } from '@df/ui/price/price.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import debounce from 'lodash-es/debounce';
import { ExpandableComponent } from 'projects/app-default/src/app/ui/display/expandable/expandable.component';

@Component({
    selector: 'ac-filter-attribute-range',
    templateUrl: '../../../../catalog/ui/listing/filter/filter-attribute-range.html',
    standalone: true,
    imports: [ExpandableComponent, ZippyModule, TranslationModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedCommerceFilterAttributeRangeComponent extends AdvancedCommerceListingFiltersAttributeComponentDef {
    private priceService = inject(PriceService);
    private translationService = inject(TranslationService);

    zippyId() {
        return ['filter', this.filter().id];
    }

    handleModelChangeFrom = debounce(this.handleModelChangeFromDebounced, 300);
    handleModelChangeTo = debounce(this.handleModelChangeToDebounced, 300);
    minMax = computed(() => {
        return this.store.filtersPriceTo() - 1 || this.filter().meta?.max || 0;
    });
    maxMin = computed(() => {
        return this.store.filtersPriceFrom() + 1 || this.filter().meta?.min || 0;
    });

    get getSelectedOptionsForFilter(): string {
        const min = this.minModel;
        const max = this.maxModel;
        let output = '';
        if (min) {
            output += `${this.translationService.transform('from')} ${this.priceService.toHtmlString(min)}`;
        }
        if (max) {
            if (min) {
                output += ' ';
            }
            output += `${this.translationService.transform('to')} ${this.priceService.toHtmlString(max)}`;
        }
        return output;
    }

    protected handleModelChangeFromDebounced(value?: number): void {
        const min = this.filter().meta?.min || 0;
        if (!value) {
            value = min;
        }
        if (value < min) {
            value = min;
        } else if (value > this.minMax()) {
            value = this.minMax();
        }
        this.store.filtersPriceFromSet(this.filter(), value);
        this.changeDetectorRef.detectChanges();
    }

    protected handleModelChangeToDebounced(value?: number): void {
        const max = this.filter().meta?.max || 0;
        if (!value) {
            value = max;
        }
        if (value > max) {
            value = max;
        } else if (value < this.maxMin()) {
            value = this.maxMin();
        }
        this.store.filtersPriceToSet(this.filter(), value);
        this.changeDetectorRef.detectChanges();
    }

    get min(): number | undefined {
        return this.filter().meta?.min;
    }

    get max(): number | undefined {
        return this.filter().meta?.max;
    }

    get minModel(): number | undefined {
        return this.store.filtersPriceFrom();
    }

    set minModel(value: number | undefined) {
        if (!!this.filter().meta) {
            this.handleModelChangeFrom(value);
        }
    }

    get maxModel(): number | undefined {
        return this.store.filtersPriceTo();
    }

    set maxModel(value: number | undefined) {
        if (!!this.filter().meta) {
            this.handleModelChangeTo(value);
        }
    }

    get label(): string | undefined {
        return this.filter().label;
    }

    onMinChange(value): void {
        this.store.filtersPriceFrom();
    }

    onMaxChange(value): void {
        this.store.filtersPriceTo();
    }
}
